import { zodResolver } from '@hookform/resolvers/zod'
import type React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'
import { InputText } from '@/components/commons/input-text'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { Container } from '@/components/ui/container'

import { AirbnbIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useSetPassword } from '@/features/auth/queries/use-set-password'
import {
	SET_PASSWORD_FORM_MODEL,
	SetPasswordFormSchema,
	type SetPasswordValidationSchema
} from '@/features/auth/set-password-form-model'
import { errorHandler } from '@/helpers/utils'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'

export const SetPassword: React.FC = () => {
	const { t } = useTranslation()
	const { token } = useParams()
	const navigate = useNavigate()
	const {
		register,
		handleSubmit,
		formState: { errors, touchedFields }
	} = useForm({
		mode: 'onBlur',
		resolver: zodResolver(SetPasswordFormSchema)
	})

	//reset password mutation
	const { mutate: resetPassword, isPending } = useSetPassword({
		onError: (e) => errorHandler(e), //toast.error(t(e.message)),
		onSuccess: () => {
			navigate(routes.LOGIN.path)
			toast.success(t('set_password:success_message'))
		}
	})

	const onSubmit: SubmitHandler<SetPasswordValidationSchema> = (data) => {
		resetPassword({ password: data.password, token: token ?? '' })
	}

	return (
		<Container>
			<title>{t('set_password:page_title_meta')}</title>
			<div className='flex h-full items-center justify-center'>
				<div className='flex w-[360px] flex-col items-stretch'>
					<div className='flex justify-center'>
						<AirbnbIcon className='fill-primary-600' size={40} />
					</div>
					<h4 className='mt-6 text-center font-bold text-display-sm text-neutral-900'>
						{t('set_password:title')}
					</h4>
					<p className='my-2 mb-14 text-center text-md'>
						{t('set_password:subtitle')}
					</p>
					<form
						className='flex flex-col items-stretch gap-4'
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className='flex flex-col gap-4'>
							<InputText
								autoComplete={'new-password'}
								errorMessage={t(
									(errors.password?.message || '') as TKey
								)}
								label={t(
									SET_PASSWORD_FORM_MODEL.password.label
								)}
								placeholder={t(
									SET_PASSWORD_FORM_MODEL.password.placeholder
								)}
								touched={touchedFields.password}
								type={'password'}
								visibilityToggle
								{...register(
									SET_PASSWORD_FORM_MODEL.password.name
								)}
								helpText={t(
									SET_PASSWORD_FORM_MODEL.password
										.helpText as TKey
								)}
							/>
							<InputText
								autoComplete={'repeat new-password'}
								errorMessage={t(
									(errors.confirm_password?.message ||
										'') as TKey
								)}
								label={t(
									SET_PASSWORD_FORM_MODEL.confirm_password
										.label
								)}
								placeholder={t(
									SET_PASSWORD_FORM_MODEL.confirm_password
										.placeholder
								)}
								touched={touchedFields.confirm_password}
								type={'password'}
								visibilityToggle
								{...register(
									SET_PASSWORD_FORM_MODEL.confirm_password
										.name
								)}
								helpText={t(
									SET_PASSWORD_FORM_MODEL.confirm_password
										.helpText as TKey
								)}
							/>
						</div>

						<div className='flex flex-col items-stretch gap-2'>
							<ButtonBrandLegacy
								disabled={isPending}
								size={'md'}
								type={'submit'}
							>
								{t('set_password:primary_cta')}
								{isPending && <Spinner size={16} />}
							</ButtonBrandLegacy>
						</div>
					</form>
				</div>
			</div>
		</Container>
	)
}

SetPassword.displayName = 'SetPassword'
